// A Trade Story someone was SENT. Reached at /story/<slug>/<token>.
//
// The link is shareable, not broadcast — Alan, 2026-08-04: "when we share
// publicly that's not the intent, that's the side effect". So the reader
// lands on a page that says plainly what they've been sent and who sent it,
// and gives a name and an email before the trade itself opens. That turns an
// anonymous URL into a named recipient, and the firm gets a subscriber
// instead of an untraceable read.
//
// Once unlocked, the signed key is kept locally so a refresh or a return
// visit doesn't re-ask — the gate is there to identify a reader, not to
// nag one.
import { useEffect, useState } from "react";
import { API_BASE } from "../api";
import { StoryBody, type Story } from "./TradeStory";

type Teaser = {
  symbol: string; book: string; money: "real" | "paper";
  shared_at: string | null; firm: string;
};
type Unlocked = Story & { shared: { symbol: string; book: string; shared_at: string | null; views: number };
                          key: string };

const keyFor = (token: string) => `stonky.story.${token}`;

export default function PublicStory({ token }: { token: string }) {
  const [teaser, setTeaser] = useState<Teaser | null>(null);
  const [story, setStory] = useState<Unlocked | null>(null);
  const [gone, setGone] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      // A key from a previous visit means this reader already identified
      // themselves — go straight in.
      const saved = localStorage.getItem(keyFor(token));
      if (saved) {
        try {
          const r = await fetch(`${API_BASE}/api/public/story/${token}?key=${encodeURIComponent(saved)}`);
          if (r.ok) { const d = await r.json(); if (live) { setStory({ ...d, key: saved }); return; } }
          localStorage.removeItem(keyFor(token)); // expired or revoked
        } catch { /* fall through to the gate */ }
      }
      try {
        const r = await fetch(`${API_BASE}/api/public/story/${token}/teaser`);
        if (!r.ok) { if (live) setGone(true); return; }
        if (live) setTeaser(await r.json());
      } catch { if (live) setGone(true); }
    })();
    return () => { live = false; };
  }, [token]);

  async function unlock(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/story/${token}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: name, email, daily_opt_in: optIn }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(typeof j.detail === "string" ? j.detail : "Couldn’t open that story.");
      }
      const d = await r.json();
      localStorage.setItem(keyFor(token), d.key);
      setStory(d);
    } catch (e2) { setErr((e2 as Error).message); }
    finally { setBusy(false); }
  }

  if (gone) {
    return (
      <Shell>
        <div className="text-center space-y-3 max-w-md mx-auto py-16">
          <p className="text-slate-400 font-sans">
            This story isn’t available. The link may have been taken down, or it never existed.
          </p>
          <a className="btn inline-block" href="/share">Read the Bull Sheet →</a>
        </div>
      </Shell>
    );
  }

  if (story) {
    return (
      <Shell>
        <div className="text-xs text-slate-500 font-sans">
          Shared with you{story.shared.shared_at
            ? ` on ${new Date(story.shared.shared_at).toLocaleDateString()}`
            : ""}. One trade, in full — the reasoning that led to it and what happened after.
        </div>
        <StoryBody story={story} />
        <div className="border-t border-edge pt-4 text-center space-y-2">
          <p className="text-sm text-slate-400 font-sans">
            Every trade this firm makes is recorded like this — the macro read, the board that
            ruled on it, the directive, the order, and the post-mortem.
          </p>
          <a className="btn btn-primary inline-block" href="/share">See today’s Bull Sheet →</a>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-lg mx-auto py-10 space-y-5">
        {teaser ? (
          <>
            <div className="space-y-2 text-center">
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                {teaser.firm} shared a trade with you
              </div>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <span className="text-3xl font-black">{teaser.symbol}</span>
                <span className={`badge ${teaser.money === "real"
                  ? "bg-down/20 text-down" : "bg-accent/20 text-accent"}`}>
                  {teaser.money === "real" ? "REAL MONEY" : "PAPER"}
                </span>
              </div>
              <p className="text-sm text-slate-400 font-sans">
                You’re about to read the full record of one trade: the macro read behind it, the
                board that ruled on it, the order that hit the market, what it made or lost, and
                the post-mortem that judged it.
              </p>
            </div>

            <form onSubmit={unlock} className="card space-y-3">
              <div className="label mb-0">Who’s reading?</div>
              <input className="input w-full" placeholder="First name" value={name} required
                     onChange={(e) => setName(e.target.value)} />
              <input className="input w-full" placeholder="you@example.com" type="email" value={email}
                     required onChange={(e) => setEmail(e.target.value)} />
              <label className="flex items-start gap-2 text-xs text-slate-400 font-sans">
                <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} />
                <span>Send me the Bull Sheet — the firm’s daily note. Unsubscribe any time.</span>
              </label>
              {err && <div className="text-down text-xs">{err}</div>}
              <button className="btn btn-primary w-full" disabled={busy}>
                {busy ? "Opening…" : "Read the trade"}
              </button>
              <p className="text-[11px] text-slate-600 font-sans">
                Your name and email go to the firm that shared this, so they know who read it.
              </p>
            </form>
          </>
        ) : (
          <div className="text-slate-500 text-center">loading…</div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-ink">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-3 border-b border-edge bg-ink/95 backdrop-blur no-print">
        <div className="flex items-baseline gap-3">
          <span className="font-black tracking-tight uppercase text-sm">Stonky</span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Shared trade story</span>
        </div>
        <a className="btn text-xs" href="/share">The Bull Sheet →</a>
      </div>
      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6">{children}</div>
    </div>
  );
}
